import type { AppError } from 'portlet/common/errors';
import type { FilterValue } from 'portlet/models';
import type { ActionType } from 'portlet/store/performance';

import type { SettingFormDefinition } from './settings';

export type ActionTransition = {
    from: ActionType;
    to: ActionType;
    deltaTime: number;
    description: string;
};

export interface PerformanceCategorySummary {
    key: 'prep' | 'data' | 'vis';
    title: string;
    time: number;
    threshold: number;
    description?: string;
    footer?: string;
}

export enum PortletMenuActions {
    MAXIMIZE = 'MAXIMIZE',
    SETTINGS = 'SETTINGS',
    FILTERS = 'FILTERS',
    SUPPORT = 'SUPPORT',
    INFO = 'INFO',
    PERFORMANCE = 'PERFORMANCE',
}

export type PortletDebugInformationItem = {
    readonly key: string;
    readonly label: string;
    readonly value: Record<string, unknown> | null;
};

export type PortletDebugInformation = Record<string, PortletDebugInformationItem>;

export type EmailTemplate = {
    readonly recipient: string;
    readonly subject: string;
    readonly description: string;
};

export interface FilterDateAge {
    readonly date: string;
    readonly title: string;
    readonly color: string;
}

export interface Filter {
    readonly filterKey: string;
    readonly filterValue: FilterValue;
    readonly filterKeyName: string;
    readonly filterApplied: readonly string[];
    readonly filterKeyApplied: readonly string[];
}

export interface FiltersPopupDefinition {
    readonly sections: {
        readonly dateAge: FilterDateAge;
        readonly localFilters: readonly Filter[];
        readonly userSelectedFilters: readonly Filter[];
        readonly defaultFilters: readonly Filter[];
        readonly notSetFilters: readonly string[];
        readonly dataFrom: { date: Date; isDataPossiblyCached: boolean } | null;
    };
    onReload: () => void;
    onClose: () => void;
}

export interface PerformanceStore {
    transitions: ActionTransition[];
    actionsTransitions: Record<ActionType, ActionType[]>;
    performanceCategorySummary: PerformanceCategorySummary[];
    totalDataCallTime: number;
    filtersDelayTimestamp: number;
    filtersDelayDeltaTime: number;
}

export interface PerformanceDialogDefinition {
    readonly performanceStore: PerformanceStore;
    onClose: () => void;
}

export interface EmailSupportStore {
    supportEmailData: EmailTemplate;
    loading: boolean;
    error: AppError | null;
}

export interface SupportEmailDefinition {
    readonly debugInfo: PortletDebugInformation;
    readonly supportStore: EmailSupportStore;
    onSubmit: (email: EmailTemplate, debugInfo: PortletDebugInformation) => void;
    onChange: (email: EmailTemplate) => void;
    onClose: () => void;
}

export interface InfoPopupDefinition {
    readonly debugInfo: PortletDebugInformation;
    onClose: () => void;
}

export type MenuInteractionEventPayload =
    | ({ tag: PortletMenuActions.SETTINGS } & SettingFormDefinition)
    | ({ tag: PortletMenuActions.FILTERS } & FiltersPopupDefinition)
    | ({ tag: PortletMenuActions.SUPPORT } & SupportEmailDefinition)
    | { tag: PortletMenuActions.INFO; onClose: () => void; debugInfo: PortletDebugInformation }
    | { tag: PortletMenuActions.MAXIMIZE }
    | ({ tag: PortletMenuActions.PERFORMANCE } & PerformanceDialogDefinition);

export * from './settings';
export * from './settingsCodecs';
export * from './interactiveTitles';
